import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { keyframes, useTheme } from '@mui/material/styles';
import { ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductIndividualDto } from 'api/responses/models/UnitCreation/ProductIndividualSearchResultDto';
import { OperatingHoursDisplayComponent } from 'assets/volvoIcons/ProductIndividual/OperatingHoursDisplayComponent';
import { UnitCreationCard } from 'library/components/controls/cards/UnitCreationCard';
import { IControl } from 'library/components/controls/common/IControl';
import { Removable } from 'library/types';
import { PCTheme, textOverflowSx } from 'library/utils/themes/PCTheme';
import { productIndividualCampaigns } from 'main/routes/urls/RouteUrls';
import { ProductIndividualLink } from 'modules/ProductIndividualview/Components/ProductIndividualTitleLink';
import { CampaignWarning } from 'modules/Shared/Components/CampaignWarning';

import { ProductIndividualCardSNField } from './ProductIndividualCardSNField';

const blink = keyframes(`
	from { border-color: #1c6bba; background: #004fbc14; }
	to { border-color: #0000001f; }
`);

const {
	palette: { grey },
} = PCTheme;

interface ProductIndividualCardProps extends IControl, Removable {
	actions?: ReactNode;
	isErrored?: boolean;
	isFromSearchRequest?: boolean;
	product: ProductIndividualDto;
	SNField?: ReactNode;
}

export const ProductIndividualCard = memo(
	({
		actions,
		isErrored,
		isFromSearchRequest,
		isRemoved,
		product: {
			generatedUuid,
			type: productType,
			serialNumber,
			designation,
			productName,
			operatingHours,
			energyThroughput,
			latestKnownOperationHoursDateFormatted,
			campaignType,
		},
		SNField,
	}: ProductIndividualCardProps) => {
		const { t } = useTranslation(['common', 'commonButton', 'productType']);
		const {
			palette: {
				error: { main },
			},
		} = useTheme();

		return (
			<UnitCreationCard
				sx={[
					isFromSearchRequest
						? { animation: `${blink} 2s linear 1` }
						: null,
					isErrored ? { borderColor: main } : null,
					isRemoved ? { pointerEvents: 'none' } : null,
					{
						backgroundColor: isRemoved ? grey[50] : 'default',
						maxWidth: 20,
					},
				]}
				variant="outlined"
				color="default"
				data-testid={`PI-${generatedUuid}`}>
				<CardHeader
					action={
						<>
							{campaignType && (
								<IconButton>
									<ProductIndividualLink
										section={productIndividualCampaigns}
										serialNumber={serialNumber}
										sx={{ lineHeight: 0.5 }}>
										<CampaignWarning
											campaignType={campaignType}
											key={`action-campaign-${serialNumber}`}
										/>
									</ProductIndividualLink>
								</IconButton>
							)}
							{actions || <></>}
						</>
					}
					sx={{
						'.MuiCardHeader-action': {
							display: 'flex',
							alignItems: 'center',
						},
					}}
					title={
						<Typography
							variant="h6"
							component={'span'}
							sx={{
								fontSize: 16,
							}}>
							{t(`productType:${productType?.toString()}`, {
								defaultValue: 'error-unexpected',
							})}
						</Typography>
					}
				/>
				<CardContent>
					{SNField || (
						<ProductIndividualCardSNField
							serialNumber={serialNumber}
						/>
					)}
					<Typography
						component="p"
						variant="subtitle2"
						sx={[
							{
								fontWeight: 400,
								...textOverflowSx,
							},
						]}>
						{`${t('common:designation')}: `}
						<strong>{designation}</strong>
					</Typography>
					<Typography
						component="p"
						variant="subtitle2"
						sx={[
							{
								fontWeight: 400,
								...textOverflowSx,
							},
						]}>
						{`${t('common:product-name')}: `}
						<strong>{productName}</strong>
					</Typography>

					<OperatingHoursDisplayComponent
						operatingHours={operatingHours}
						energyThroughput={energyThroughput}
						operatingHoursDate={
							latestKnownOperationHoursDateFormatted
						}
					/>
				</CardContent>
			</UnitCreationCard>
		);
	}
);
